import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { ConfirmationModal, Success, Failed, Loader } from '../../components';
import {
  ProfilePageShell,
  ProfileContent,
  ProfileHead,
  ProfileSection,
  ProfileGrid,
  ProfileField,
  ProfilePhoneField,
  ProfileSelect,
  profileInputSx as inputSx,
} from '../../components/ProfileLayout';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Country, State, City } from 'country-state-city';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getProfileDetails } from '../../rtk-apis/profile/getProfileDetails';
import { ClinicProfileValidationSchema } from '../auth/helper/Schema';
import { editClinicApi } from '../../rtk-apis/clinics/editClinic';
import { clearState } from '../../store/slices/clinics/editClinic';
import { getEmail } from '../../Utils/authUtils';

const ClinicProfile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [clinicData, setClinicData] = useState({});
  const [displayMessage, setDisplayMessage] = useState('');
  const [OpenSuccessModal, setOpenSuccessModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [OpenFailureModal, setOpenFailureModal] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const [countryCode, setCountryCode] = useState('+61');

  // ****************** REDUX STATES **********************************

  const { data, loading } = useSelector((state: RootState) => state.getProfileDetailsResponse);

  const {
    status: updateClinicStatus,
    result: updateClinicResult,
    msg: updateClinicMessage,
    loading: updateClinicLoading,
  } = useSelector((state: RootState) => state.editClinicResponse);

  useEffect(() => {
    if (Object.keys(data)?.length > 0) {
      setClinicData(data);
      setSelectedCountry(data?.Country);
      setSelectedState(data?.State);
      setSelectedCity(data?.City);
      // Show the saved code; save sends this value, so a stale default would overwrite it.
      const saved = (data as unknown as { countryCode?: string })?.countryCode;
      if (saved) {
        setCountryCode(saved);
      }
    }
  }, [data]);

  const initialValues = {
    name: clinicData?.name ? clinicData?.name : '',
    email: clinicData?.email ? clinicData?.email : '',
    countryCode: clinicData?.countryCode ? clinicData?.countryCode : '',
    contactNumber: clinicData?.contactDetails ? clinicData?.contactDetails : '',
    address: clinicData?.Address ? clinicData?.Address : '',
    zipCode: clinicData?.ZipCode ? clinicData?.ZipCode : '',
    country: clinicData?.Country ? clinicData?.Country : '',
    state: clinicData?.State ? clinicData?.State : '',
    city: clinicData?.City ? clinicData?.City : '',
  };

  // ******************* Use-Effects *************************************************

  useEffect(() => {
    getClinicDetails();
  }, [updateClinicStatus]);

  // Update states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry);
      setStateOptions(states);
      setCityOptions([]); // Reset city options when country changes
    }
  }, [selectedCountry]);

  // Update cities when state changes
  useEffect(() => {
    if (selectedState) {
      const cities = City.getCitiesOfState(selectedCountry, selectedState);
      setCityOptions(cities);
    }
  }, [selectedState, selectedCountry]);

  // ******************************************************************************

  const getClinicDetails = () => {
    const userEmail = getEmail();
    const payload = {
      email: userEmail,
    };
    dispatch(getProfileDetails(payload));
  };

  const handleSubmit = () => {
    if (formValues) {
      setOpenConfirmationModal(false);
      const payload = {
        id: clinicData?._id || '',
        name: formValues.name,
        email: formValues.email.toLowerCase(),
        countryCode: countryCode,
        contactDetails: formValues.contactNumber,
        Address: formValues.address,
        ZipCode: formValues.zipCode,
        Country: formValues.country,
        State: formValues.state,
        City: formValues.city,
      };
      dispatch(editClinicApi(payload));
    }
  };

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setOpenSuccessModal(true);
      setTimeout(() => {
        setOpenSuccessModal(false);
        setDisplayMessage('');
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setOpenFailureModal(true);
      setTimeout(() => {
        setOpenFailureModal(false);
        setDisplayMessage('');
      }, 1500);
    };

    if (updateClinicStatus === 'SUCCESS') {
      handleSuccess(updateClinicMessage);
    } else if (updateClinicStatus === 'FAILED') {
      handleFailure(updateClinicMessage);
    }

    return () => {
      dispatch(clearState());
    };
  }, [updateClinicMessage, updateClinicStatus]);

  return (
    <>
      <ProfilePageShell>
        <Formik
          initialValues={initialValues}
          validationSchema={ClinicProfileValidationSchema}
          onSubmit={values => {
            setFormValues(values);
            setOpenConfirmationModal(true);
          }}
          enableReinitialize
        >
          {({ setFieldValue, values, errors, touched, handleChange, handleBlur }) => {
            const err = (k: string) => (touched[k] && (errors[k] as string)) || '';
            const textField = (name: string, placeholder: string, extra = {}) => (
              <TextField
                fullWidth
                name={name}
                placeholder={placeholder}
                value={values[name] || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!err(name)}
                helperText={err(name)}
                sx={inputSx}
                {...extra}
              />
            );
            return (
              <Form autoComplete="off">
                <ProfileContent>
                  <ProfileHead
                    title="Update Clinic Information"
                    onCancel={() => navigate('/dashboard')}
                  />

                  <ProfileSection title="Clinic Profile Details">
                    <ProfileGrid>
                      <ProfileField
                        label="Name"
                        required
                      >
                        {textField('name', 'Name')}
                      </ProfileField>

                      <ProfileField
                        label="Email Address"
                        required
                      >
                        {textField('email', 'Email Address', { autoComplete: 'off' })}
                      </ProfileField>

                      <ProfileField label="Address">
                        {textField('address', 'Address', { autoComplete: 'off' })}
                      </ProfileField>

                      <ProfileField label="Zip Code">
                        {textField('zipCode', 'Zip Code', { inputProps: { maxLength: 10 } })}
                      </ProfileField>

                      <ProfileField label="Country">
                        <ProfileSelect
                          name="country"
                          value={selectedCountry}
                          onChange={v => {
                            setSelectedCountry(v);
                            setFieldValue('country', v);
                            setFieldValue('state', '');
                            setFieldValue('city', '');
                            setSelectedState('');
                            setSelectedCity('');
                          }}
                          options={Country.getAllCountries().map(c => ({
                            value: c.isoCode,
                            label: c.name,
                          }))}
                        />
                      </ProfileField>

                      <ProfileField label="State">
                        <ProfileSelect
                          name="state"
                          value={selectedState}
                          onChange={v => {
                            setSelectedState(v);
                            setFieldValue('state', v);
                            setFieldValue('city', '');
                            setSelectedCity('');
                          }}
                          options={stateOptions.map(st => ({
                            value: st.isoCode,
                            label: st.name,
                          }))}
                          disabled={!selectedCountry}
                        />
                      </ProfileField>

                      <ProfileField label="City">
                        <ProfileSelect
                          name="city"
                          value={selectedCity}
                          onChange={v => {
                            setSelectedCity(v);
                            setFieldValue('city', v);
                          }}
                          options={cityOptions.map(ci => ({ value: ci.name, label: ci.name }))}
                          disabled={!selectedState}
                        />
                      </ProfileField>

                      <ProfileField
                        label="Contact Number"
                        required
                      >
                        <ProfilePhoneField
                          name="contactNumber"
                          value={values.contactNumber}
                          countryCode={countryCode}
                          onCountryCodeChange={setCountryCode}
                          onChange={digits => setFieldValue('contactNumber', digits)}
                          onBlur={handleBlur}
                          error={err('contactNumber')}
                        />
                      </ProfileField>
                    </ProfileGrid>
                  </ProfileSection>
                </ProfileContent>
              </Form>
            );
          }}
        </Formik>
      </ProfilePageShell>
      <Loader isLoading={loading || updateClinicLoading} />
      <ConfirmationModal
        title="Update clinic profile!"
        description="Are you sure you want to update the clinic profile?"
        isConfirmationModalOpen={openConfirmationModal}
        handleCloseModal={() => {
          setOpenConfirmationModal(false);
        }}
        handleConfirm={handleSubmit}
        cancelActionText="No, Cancel"
        confirmActionText="Yes, Confirm"
        isPatientReport={true}
      />
      <Success
        isSuccessModalOpen={OpenSuccessModal}
        handleCloseModal={() => {
          setOpenSuccessModal(false);
        }}
        title={displayMessage}
      />
      <Failed
        isFailedModalOpen={OpenFailureModal}
        handleCloseModal={() => {
          setOpenFailureModal(false);
        }}
        title={displayMessage}
      />
    </>
  );
};

export default ClinicProfile;
